import { Link } from 'react-router-dom';
import { services } from '../../../mocks/services';

const CARD_BADGES: Record<number, { label: string; color: string }> = {
  1: { label: '인기', color: 'from-purple-500 to-purple-700' },
  4: { label: 'NEW', color: 'from-primary-gold to-amber-500' },
  5: { label: '추천', color: 'from-emerald-500 to-teal-600' },
};

export default function ServiceCards() {
  return (
    <section className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 stars-bg opacity-30"></div>
      {/* Subtle gradient lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-gold/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-gold/20 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-gold/10 border border-primary-gold/25 text-primary-gold/80 text-xs tracking-widest uppercase mb-5 font-medium">
            <span className="w-1 h-1 bg-primary-gold rounded-full"></span>
            운명 분석 서비스
            <span className="w-1 h-1 bg-primary-gold rounded-full"></span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            운명을 읽는 <span className="text-gradient">여섯 가지 방법</span>
          </h2>
          <p className="text-base sm:text-lg text-purple-300 max-w-2xl mx-auto">
            점성술, 사주팔자, 꿈해몽으로 당신의 과거, 현재, 미래를 정확하게 분석합니다
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => {
            const badge = CARD_BADGES[service.id];
            return (
              <Link
                key={service.id}
                to={service.link}
                className="group relative glass-card rounded-3xl overflow-hidden animate-slide-up cursor-pointer"
                style={{
                  animationDelay: `${index * 100}ms`,
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(251,191,36,0.35)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(251,191,36,0.12), 0 0 80px rgba(139,92,246,0.10), 0 8px 32px rgba(0,0,0,0.5)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                {/* Popularity Badge */}
                {badge && (
                  <div className="absolute top-4 left-4 z-20">
                    <span className={`text-[10px] font-black px-2.5 py-1 rounded-full bg-gradient-to-r ${badge.color} text-white tracking-wide`}>
                      {badge.label}
                    </span>
                  </div>
                )}

                {/* Card Image */}
                <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-108 transition-transform duration-700"
                    style={{ transition: 'transform 0.7s ease' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/75"></div>
                  {/* Shimmer overlay on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500"
                    style={{
                      background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, transparent 50%, rgba(139,92,246,0.08) 100%)',
                    }}
                  />
                  {/* Token Badge */}
                  <div className="absolute top-4 right-4 flex items-center space-x-1 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full border border-primary-gold/35">
                    <i className="ri-coin-line text-primary-gold text-sm"></i>
                    <span className="text-primary-gold text-xs font-semibold">{service.tokens} 토큰</span>
                  </div>
                </div>

                {/* Glow border on hover */}
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500"
                  style={{
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.06) 0%, transparent 40%, rgba(139,92,246,0.08) 100%)',
                  }}
                />

                {/* Content */}
                <div className="relative z-10 p-7 sm:p-8">
                  {/* Icon + Title Row */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div
                      className="w-13 h-13 flex items-center justify-center rounded-2xl flex-shrink-0 transition-all duration-400 group-hover:scale-110 group-hover:-rotate-6"
                      style={{
                        background: 'linear-gradient(135deg, rgba(251,191,36,0.18) 0%, rgba(139,92,246,0.18) 100%)',
                        border: '1px solid rgba(251,191,36,0.25)',
                        width: '52px',
                        height: '52px',
                        transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(251,191,36,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                      }}
                    >
                      <span className="text-2xl sm:text-3xl leading-none select-none">{service.emoji}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-gradient transition-all duration-300 leading-tight">
                      {service.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-purple-300/90 mb-5 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-purple-200/90">
                        <i className="ri-checkbox-circle-fill text-primary-gold mt-0.5 flex-shrink-0 text-xs"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-primary-gold font-semibold text-sm group-hover:translate-x-1.5 transition-transform duration-300">
                      <span>시작하기</span>
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 translate-x-2"
                      style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}
                    >
                      <i className="ri-arrow-right-line text-primary-gold text-xs"></i>
                    </div>
                  </div>
                </div>

                {/* Decorative Corner Glow */}
                <div className="absolute bottom-0 right-0 w-24 h-24 rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: 'radial-gradient(circle at bottom right, rgba(251,191,36,0.12), transparent 70%)' }}
                />
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14 sm:mt-18 animate-fade-in delay-400">
          <p className="text-purple-400/60 text-sm mb-6">지금 바로 시작해보세요 — 첫 분석은 무료입니다</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/astrology"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-primary-purple to-primary-light rounded-full text-white font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-primary-purple/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-sparkling-line"></i>
              <span>지금 바로 운세 확인하기</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/annual-report"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full text-black font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-2-line"></i>
              <span>1년 운세 리포트</span>
            </Link>
            <Link
              to="/lifetime-report"
              className="inline-flex items-center space-x-3 px-8 py-4 glass-card border border-primary-gold/40 rounded-full text-primary-gold font-bold text-base sm:text-lg hover:shadow-2xl hover:border-primary-gold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-time-line"></i>
              <span>평생 운세 리포트</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
